import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingGroup, Spinner } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Bold, Paragraph } from "capitalrx-components-2.0/lib/typography";
import { formatDate } from "capitalrx-utilities";

import { useListMemberNotificationsQuery } from "@/App/api/notification";
import { ContentPanel, NoResults, PageWrapper, Pager } from "@/App/components";
import { useNavigateClient } from "@/App/hooks";

import styled from "styled-components";

const MessageRow = styled.div`
  border-bottom: 1px solid #cfd9e1;
  padding: 16px;

  &:hover {
    cursor: pointer;
    background-color: rgba(165, 202, 235, 0.1);
  }
`;

export const InboxPage: FC = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigateClient();
  const [pageNumber, setPageNumber] = useState(1);
  const { data: allMessages, isLoading: isLoadingMessages } = useListMemberNotificationsQuery({
    page_number: pageNumber,
    results_per_page: 10,
  });

  const { isCompressed } = useResponsive({
    isCompressed: {
      mobile: true,
      desktop: false,
    },
  });

  const ContentFlowContainer = isCompressed ? Stack : Shelf;

  const messages = useMemo(() => {
    return (allMessages?.results || []).map((m) => m.member_notification);
  }, [allMessages]);

  const onNotificationSelected = useCallback((notificationId: number) => {
    navigate(`/inbox/${notificationId}`);
  }, []);

  return (
    <PageWrapper>
      <ContentPanel title={translate("INBOX_PAGE.messages")} showBorder={false}>
        <LoadingGroup loading={isLoadingMessages}>
          <LoadingGroup.Placeholder
            fallback={
              <Shelf>
                <Shelf.Spacer />
                <Spinner />
                <Shelf.Spacer />
              </Shelf>
            }
          >
            <Stack>
              <Stack spacing="none">
                {messages.map((m) => {
                  return (
                    <MessageRow key={m.id} onClick={() => onNotificationSelected(m.id)}>
                      <ContentFlowContainer>
                        {m.read_status === "unread" && <Bold testId={`message-${m.id}-subject`}>{m.subject.startsWith("MESSAGE_DETAIL.") ? translate(m.subject) : m.subject}</Bold>}
                        {m.read_status !== "unread" && <Paragraph testId={`message-${m.id}-subject`}>{m.subject.startsWith("MESSAGE_DETAIL.") ? translate(m.subject) : m.subject}</Paragraph>}
                        <Shelf.Spacer />
                        {formatDate(m.created_at, "MMMM D, YYYY")}
                      </ContentFlowContainer>
                    </MessageRow>
                  );
                })}
                {messages.length === 0 && <NoResults message={translate("INBOX_PAGE.youHaveNoMessages")} />}
              </Stack>
              {messages.length > 0 && (
                <Shelf>
                  <Shelf.Spacer />
                  <Pager
                    pageNumber={pageNumber}
                    setPageNumber={setPageNumber}
                    disabled={isLoadingMessages}
                    resultCount={allMessages?.result_count}
                    resultsPerPage={10}
                  />
                  <Shelf.Spacer />
                </Shelf>
              )}
            </Stack>
          </LoadingGroup.Placeholder>
        </LoadingGroup>
      </ContentPanel>
    </PageWrapper>
  );
};
